import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ActionBar, ActionData } from './components/ActionBar/ActionBar';
import { withWindow } from '@shared/utils/withWindow';
import { InlineEditorWrapper } from './InlineEditor.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFeatureValue } from '@shared/core/featureFlags';

type InlineEditorProps = {
  elementLabel: string;
  actionData: ActionData;
  wrapperType?: 'actionInside' | 'actionOutside';
};

export const InlineEditor: FC<InlineEditorProps> = props => {
  const { elementLabel, actionData, wrapperType = 'actionOutside', children } = props;

  const [isEditing, setIsEditing] = useState(false);

  const queryParamHelper = useQueryParamHelper();

  const adminDashboardInlineEditingEnabled = useFeatureValue('adminDashboardInlineEditing').value === 'on';

  const inlineEditingEnabled = useMemo(() => queryParamHelper.getValue('enableInlineEditing') === 'true', [queryParamHelper]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const childWrapperRef = useRef<HTMLDivElement>(null);

  const isPreviewing = isInIframe();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const el = e.target;
      const domNode = wrapperRef.current;

      if (!(domNode && el instanceof Node && domNode.contains(el)) && isEditing) {
        setIsEditing(false);
      }
    };
    if (isEditing) {
      withWindow(window => {
        window.document.addEventListener('mousedown', handleClickOutside);
      });
    }
    return () => {
      withWindow(window => {
        window.document.removeEventListener('mousedown', handleClickOutside);
      });
    };
  }, [isEditing]);

  const handleOnClick = useEventCallback(() => {
    // toggles the edit mode
    setIsEditing(prev => !prev);
  });

  return (
    <>
      {adminDashboardInlineEditingEnabled && inlineEditingEnabled ? (
        <InlineEditorWrapper
          isPreviewing={isPreviewing}
          position="relative"
          ref={wrapperRef}
          _after={isEditing && wrapperType === 'actionInside' ? { content: '""', inset: 0, position: 'absolute', background: 'rgba(78, 6, 242, 0.20)' } : {}}
          _hover={!isEditing && wrapperType === 'actionInside' ? { _after: { content: '""', position: 'absolute', inset: 0, background: 'rgba(78, 6, 242, 0.10)' } } : {}}
          _active={!isEditing && wrapperType === 'actionInside' ? { _after: { content: '""', position: 'absolute', inset: 0, background: 'rgba(78, 6, 242, 0.20)' } } : {}}
        >
          <Box
            ref={childWrapperRef}
            minHeight={childWrapperRef.current?.firstElementChild?.clientHeight}
            _hover={
              !isEditing
                ? {
                    _after: {
                      border: '2px solid #4E06F2',
                      position: 'absolute',
                      inset: wrapperType === 'actionOutside' ? pxToRem(-16) : 0,
                      margin: wrapperType === 'actionOutside' ? 0 : 3,
                      borderRadius: 3,
                      background: wrapperType === 'actionOutside' ? 'rgba(78, 6, 242, 0.10)' : ''
                    },
                    _before: {
                      content: `"${elementLabel}"`,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      color: 'white',
                      top: wrapperType === 'actionOutside' ? pxToRem(-45) : 5,
                      left: wrapperType === 'actionOutside' ? pxToRem(-17) : 5,
                      backgroundColor: '#4E06F2',
                      fontFamily: 'Inter UI',
                      fontSize: pxToRem(13),
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: pxToRem(18.2),
                      letterSpacing: pxToRem(-0.032),
                      borderRadius: 3,
                      paddingX: 3,
                      height: 6,
                      zIndex: 'banner'
                    }
                  }
                : {}
            }
            _active={
              !isEditing
                ? {
                    _after: {
                      border: '2px solid #4E06F2',
                      position: 'absolute',
                      inset: wrapperType === 'actionOutside' ? pxToRem(-16) : 0,
                      margin: wrapperType === 'actionOutside' ? 0 : 3,
                      borderRadius: 3,
                      background: wrapperType === 'actionOutside' ? 'rgba(78, 6, 242, 0.20)' : ''
                    },
                    _before: {
                      content: `"${elementLabel}"`,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      color: 'white',
                      top: pxToRem(-45),
                      left: pxToRem(-17),
                      backgroundColor: '#4E06F2',
                      fontFamily: 'Inter UI',
                      fontSize: pxToRem(13),
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: pxToRem(18.2),
                      letterSpacing: pxToRem(-0.032),
                      borderRadius: 3,
                      paddingX: 3,
                      height: 6,
                      zIndex: 'banner'
                    }
                  }
                : {}
            }
            _after={
              isEditing
                ? {
                    content: '""',
                    border: '2px solid #4E06F2',
                    position: 'absolute',
                    inset: wrapperType === 'actionOutside' ? pxToRem(-16) : 0,
                    margin: wrapperType === 'actionOutside' ? 0 : 3,
                    borderRadius: 3
                  }
                : { content: '""' }
            }
            onClick={handleOnClick}
            cursor="pointer"
          >
            {children}
          </Box>
          <Box position="absolute" zIndex="banner" width="100%" top={wrapperType === 'actionOutside' ? pxToRem(-80) : '50%'} left="50%" transform="translate(-50%)">
            {isEditing && <ActionBar actionData={actionData} actionView={wrapperType === 'actionOutside' ? 'compact' : 'detailed'} />}
          </Box>
        </InlineEditorWrapper>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

InlineEditor.displayName = 'InlineEditor';
